import { useEffect } from 'react';
import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';

/**
 * Hook to track web vitals and additional performance metrics.
 * 
 * @param {Function} onPerfEntry Callback function to handle performance entries.
 */
const useWebVitals = (onPerfEntry) => {
  useEffect(() => {
    if (onPerfEntry && onPerfEntry instanceof Function) {
      // Core Web Vitals
      getCLS(onPerfEntry);  // Cumulative Layout Shift
      getFID(onPerfEntry);  // First Input Delay
      getFCP(onPerfEntry);  // First Contentful Paint
      getLCP(onPerfEntry);  // Largest Contentful Paint
      getTTFB(onPerfEntry); // Time to First Byte

      // Additional Performance Metrics
      if ('performance' in window && 'memory' in window.performance) {
        const memory = window.performance.memory;
        onPerfEntry({
          name: 'memory',
          value: memory.usedJSHeapSize,
          rating: memory.usedJSHeapSize < memory.jsHeapSizeLimit * 0.9 ? 'good' : 'poor',
          delta: 0,
          id: 'memory',
        });
      }

      // Resource Timing
      if ('performance' in window && 'getEntriesByType' in window.performance) {
        const resources = window.performance.getEntriesByType('resource');
        const resourceMetrics = resources.reduce((acc, resource) => ({
          totalSize: acc.totalSize + (resource.encodedBodySize || 0),
          totalTime: acc.totalTime + (resource.duration || 0),
          count: acc.count + 1,
        }), { totalSize: 0, totalTime: 0, count: 0 });

        onPerfEntry({
          name: 'resources',
          value: resourceMetrics.totalTime,
          rating: resourceMetrics.totalTime < 3000 ? 'good' : 'poor',
          delta: 0,
          id: 'resources',
          details: resourceMetrics,
        });
      }

      // First Paint
      if ('performance' in window && 'getEntriesByName' in window.performance) {
        const paint = window.performance.getEntriesByName('first-paint');
        if (paint.length > 0) {
          onPerfEntry({
            name: 'FP',
            value: paint[0].startTime,
            rating: paint[0].startTime < 1000 ? 'good' : 'poor',
            delta: 0,
            id: 'first-paint',
          });
        }
      }
    }
  }, [onPerfEntry]);
};

export default useWebVitals;
